import React, { useState, useRef, useEffect, useMemo } from "react";
import { customArray } from "country-codes-list";
import Icon from "../Icons";

interface Country {
  name: string;
  code: string;
  dialCode: string;
  flag: string;
}

const CountryCodeSelect: React.FC<{
  value: string;
  onChange: (dialCode: string) => void;
  className?: string;
}> = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const countries = useMemo(() => {
    return customArray({
      name: "{countryNameEn}",
      code: "{countryCode}",
      dialCode: "+{countryCallingCode}",
      flag: "{flag}",
    }) as unknown as Country[];
  }, []);

  const selectedCountry =
    countries.find((c) => c.dialCode === value) ||
    countries.find((c) => c.code === "IN")!;

  const filteredCountries = useMemo(() => {
    if (!search) return countries;
    const query = search.toLowerCase();
    return countries.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.dialCode.includes(query) ||
        c.code.toLowerCase().includes(query),
    );
  }, [search, countries]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  console.log("selectedCountry", selectedCountry);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className="flex w-28 items-center gap-1 justify-center px-3 py-2 border-none hover:bg-[#3a5063] transition-colors rounded-l-md"
        onClick={() => setIsOpen(!isOpen)}
      >
        <img
          src={`https://flagcdn.com/20x15/${selectedCountry.code.toLowerCase()}.png`}
          alt={selectedCountry.code}
          className="w-5 h-4 object-cover"
        />

        <span className="text-white text-sm">{selectedCountry.dialCode}</span>
        {/* <span className="text-gray-400 text-xs">▼</span> */}
        <Icon name="ic-down" className="w-3 h-3 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-[#2E4051] border border-gray-600 rounded-md shadow-lg z-50 max-h-72 flex flex-col">
          <div className="p-2 border-b border-gray-600">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search country..."
              className="w-full px-3 py-2 text-sm text-white bg-[#1C2C3A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#008564]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  type="button"
                  key={country.code}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#3a5063] transition-colors ${
                    selectedCountry.code === country.code ? "bg-[#008564]" : ""
                  }`}
                  onClick={() => {
                    onChange(country.dialCode);
                    setIsOpen(false);
                    setSearch("");
                  }}
                >
                  <img
                    src={`https://flagcdn.com/20x15/${country.code.toLowerCase()}.png`}
                    alt={country.code}
                    className="w-5 h-4 object-cover"
                  />
                  <span className="text-white text-sm flex-1">
                    {country.name}
                  </span>
                  <span className="text-gray-400 text-sm">
                    {country.dialCode}
                  </span>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-gray-400 text-sm text-center">
                No country found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelect;
